import { AddOutlined, CloseOutlined, DeleteOutlineOutlined, EditOutlined, RestartAltOutlined, SettingsOutlined } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Layout, Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { GetGreetingsHeader } from '../components/atoms/greetingsHeader';
import { DashboardView, WidgetDefinition, WidgetInstance } from './types';
import { findWidget } from './widgetCatalog';
import { WidgetLibrary } from './widgetLibrary';
import { RealMembersByProfileConfigDialog } from './widgets/realMembersByProfileConfigDialog';
import { GiftsByPeriodConfigDialog } from './widgets/giftsByPeriodConfigDialog';
import { NewMembersByPeriodConfigDialog } from './widgets/newMembersByPeriodConfigDialog';
import { loadDashboardViews, saveDashboardViews } from './dashboardViewService';
import { DashboardFilterProvider } from './dashboardFilterContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

const SAVE_DEBOUNCE_MS = 500;

const DEFAULT_VIEW_ID = 'default';

const DEFAULT_VIEWS: DashboardView[] = [
  {
    id: DEFAULT_VIEW_ID,
    name: 'Dashboard',
    layout: [
      // Vänster huvudområde (9 kol) — speglar /dashboard:s 9-col vänsterkolumn
      { i: 'i1', widgetId: 'real-members-stats', x: 0, y: 0, w: 4, h: 6 },
      { i: 'i2', widgetId: 'real-contacts', x: 4, y: 0, w: 3, h: 6 },
      { i: 'i3', widgetId: 'real-main-member', x: 7, y: 0, w: 2, h: 3 },
      { i: 'i4', widgetId: 'real-household-member', x: 7, y: 3, w: 2, h: 3 },
      { i: 'i5', widgetId: 'real-member-chart', x: 0, y: 6, w: 5, h: 10 },
      { i: 'i6', widgetId: 'real-autojobs', x: 5, y: 6, w: 4, h: 10 },
      // Höger sidopanel (3 kol) — speglar /dashboard:s 3-col högerkolumn (Att göra ovanpå, log under)
      { i: 'i7', widgetId: 'real-todo', x: 9, y: 0, w: 3, h: 6 },
      { i: 'i8', widgetId: 'real-member-log', x: 9, y: 6, w: 3, h: 10 },
    ],
  },
];

export const DashboardEditor = () => {
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [views, setViews] = useState<DashboardView[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [configInstance, setConfigInstance] = useState<WidgetInstance | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();
  const settingsIdRef = useRef<number>(0);
  const initialLoadDone = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editStartSnapshotRef = useRef<DashboardView[] | null>(null);

  const scope = { associationId: association?.localAss_id, districtId: district?.countyAss_id };
  const scopeKey = `${scope.associationId ?? ''}|${scope.districtId ?? ''}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // Förvälj scope när användaren är begränsad till en specifik förening eller distrikt.
  // Endast om hen har EXAKT en förening (och inga distrikt) eller tvärtom — då hamnar dashboarden på rätt scope direkt.
  // OBS: User.Association/District har ett mindre fält-set än AssociationAndDistrictSelect förväntar sig,
  // så vi kastar med "as unknown as ..." — komponenten använder bara id/namn-fälten i praktiken.
  useEffect(() => {
    if (!currentUser || association || district) return;
    if (currentUser.districts.length === 0 && currentUser.associations.length === 1) {
      setAssociation(currentUser.associations[0] as unknown as Association);
    } else if (currentUser.associations.length === 0 && currentUser.districts.length === 1) {
      setDistrict(currentUser.districts[0] as unknown as District);
    }
  }, [currentUser, association, district]);

  // Förbundsadministratör (flera distrikt/föreningar eller ingen begränsning) får redigera default-scopet.
  // En användare som hör till EN specifik förening/distrikt måste välja det innan redigering.
  const isLimitedToSingleAssociation = !!currentUser && currentUser.districts.length === 0 && currentUser.associations.length === 1;
  const isLimitedToSingleDistrict = !!currentUser && currentUser.associations.length === 0 && currentUser.districts.length === 1;
  const hasScopeRestriction = isLimitedToSingleAssociation || isLimitedToSingleDistrict;
  const canEditCurrentScope = !!scope.associationId || !!scope.districtId || !hasScopeRestriction;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    initialLoadDone.current = false;
    (async () => {
      const { views: loaded, settingsId } = await loadDashboardViews(scopeRef.current);
      if (cancelled || !isMounted()) return;
      settingsIdRef.current = settingsId;
      const initial = loaded && loaded.length ? loaded : DEFAULT_VIEWS;
      setViews(initial);
      setLoading(false);
      initialLoadDone.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, scopeKey]);

  useEffect(() => {
    if (!initialLoadDone.current) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveDashboardViews(views, settingsIdRef.current, scopeRef.current)
        .then((id) => {
          settingsIdRef.current = id;
        })
        .catch(() => undefined);
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [views]);

  const activeView = views[0];

  const updateActiveLayout = useCallback((updater: (layout: WidgetInstance[]) => WidgetInstance[]) => {
    setViews((prev) => prev.map((v, idx) => (idx === 0 ? { ...v, layout: updater(v.layout) } : v)));
  }, []);

  const scopeName = (() => {
    if (association) return association.localAss;
    if (district) return district.countyAss;
    return currentUser?.customerName ?? '';
  })();

  if (loading || !activeView) {
    return (
      <Box sx={{ pb: 6 }}>
        <Stack direction='row' alignItems='flex-end' justifyContent='space-between' sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <MainContentHeader
              isLoading={currentUser?.firstName === undefined}
              title={`${GetGreetingsHeader()}, ${currentUser?.firstName}`}
              subtitle={dayjs(new Date()).format('D MMM YYYY')}
            />
          </Box>
        </Stack>
        <Stack spacing={2}>
          <Skeleton variant='rounded' height={140} />
          <Skeleton variant='rounded' height={320} />
        </Stack>
      </Box>
    );
  }

  const handleLayoutChange = (next: Layout[]) => {
    if (!editing) return;
    updateActiveLayout((layout) =>
      layout.map((item) => {
        const match = next.find((n) => n.i === item.i);
        if (!match) return item;
        return { ...item, x: match.x, y: match.y, w: match.w, h: match.h };
      }),
    );
  };

  const handleAddWidget = (widget: WidgetDefinition) => {
    const newInstance: WidgetInstance = {
      i: `w-${Date.now()}`,
      widgetId: widget.id,
      x: 0,
      y: 0,
      w: widget.defaultW,
      h: widget.defaultH,
      config: widget.defaultConfig,
    };
    // Placera nya widgeten överst — flytta existerande nedåt så användaren ser vad som tillagts
    updateActiveLayout((layout) => [newInstance, ...layout.map((item) => ({ ...item, y: item.y + widget.defaultH }))]);
  };

  const handleRemove = (i: string) => {
    updateActiveLayout((layout) => layout.filter((item) => item.i !== i));
  };

  const handleConfigSave = (updated: WidgetInstance) => {
    updateActiveLayout((layout) => layout.map((item) => (item.i === updated.i ? updated : item)));
  };

  const handleStartEdit = () => {
    editStartSnapshotRef.current = views;
    setEditing(true);
  };

  const handleCancelEdit = () => {
    if (editStartSnapshotRef.current) setViews(editStartSnapshotRef.current);
    editStartSnapshotRef.current = null;
    setEditing(false);
    setLibraryOpen(false);
  };

  const handleSaveEdit = () => {
    editStartSnapshotRef.current = null;
    setEditing(false);
    setLibraryOpen(false);
  };

  return (
    <DashboardFilterProvider value={{ associationId: association?.localAss_id, districtId: district?.countyAss_id }}>
      <Box sx={{ pb: 6 }}>
        <Stack direction='row' alignItems='flex-end' justifyContent='space-between' sx={{ flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <MainContentHeader
              isLoading={currentUser?.firstName === undefined}
              title={`${GetGreetingsHeader()}, ${currentUser?.firstName}`}
              subtitle={dayjs(new Date()).format('D MMM YYYY')}
            />
          </Box>
          <Stack direction='row' alignItems='center' spacing={3}>
            {!editing ? (
              <Tooltip title={canEditCurrentScope ? '' : 'Välj en förening eller ett distrikt för att anpassa dess dashboard'} arrow>
                <span>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    component='button'
                    type='button'
                    underline='hover'
                    disabled={!canEditCurrentScope}
                    onClick={handleStartEdit}
                    sx={{
                      fontSize: 13,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: canEditCurrentScope ? 'primary.main' : 'text.disabled',
                      cursor: canEditCurrentScope ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <EditOutlined fontSize='inherit' />
                    Anpassa dashboard
                  </Link>
                </span>
              </Tooltip>
            ) : (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link
                component='button'
                type='button'
                underline='hover'
                onClick={handleCancelEdit}
                sx={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}
              >
                <CloseOutlined fontSize='inherit' />
                Avbryt anpassning
              </Link>
            )}
            {currentUser && (
              <Box sx={{ display: 'flex', gap: 1.5, '& .MuiAutocomplete-inputRoot': { backgroundColor: 'white' } }}>
                <AssociationAndDistrictSelect
                  hideDistrict={currentUser?.districts.length === 0}
                  association={association}
                  district={district}
                  associationCallback={(val) => setAssociation(val || undefined)}
                  districtCallback={(val) => setDistrict(val || undefined)}
                />
              </Box>
            )}
          </Stack>
        </Stack>
        {editing && (
          <Alert
            severity='info'
            sx={{
              mb: 2,
              '& .MuiAlert-action': { alignItems: 'center', pt: 0, pr: 2 },
            }}
            action={
              <Stack direction='row' spacing={1.5} sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
                <Button
                  variant='outlined'
                  disableElevation
                  size='small'
                  color='error'
                  startIcon={<RestartAltOutlined />}
                  onClick={() => setResetDialogOpen(true)}
                >
                  Återställ
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disableElevation
                  size='small'
                  startIcon={<AddOutlined />}
                  onClick={() => setLibraryOpen(true)}
                >
                  Lägg till widget
                </Button>
                <Button variant='contained' disableElevation size='small' onClick={handleSaveEdit}>
                  Spara
                </Button>
              </Stack>
            }
          >
            <AlertTitle>Redigera dashboard för {scopeName}</AlertTitle>
            Du kan nu redigera storlek, plats eller ta bort widgets. För att lägga till en ny widget, klicka på knappen Lägg till widget. Klicka på
            Spara när du är klar.
            <Box component='span' sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              Denna dashboard visas för alla användare på {scopeName}.
            </Box>
          </Alert>
        )}

        <Box
          sx={{
            // Inga animationer vid initial-rendering / layout-changes (drag/resize-feedback är fortfarande snappy)
            '& .react-grid-item': {
              transitionProperty: 'none !important',
              transitionDuration: '0s !important',
            },
            '& .react-grid-item.react-grid-placeholder': {
              transitionProperty: 'opacity !important',
            },
          }}
        >
          <ResponsiveGridLayout
            className={`fs-dashboard-grid ${editing ? 'fs-dashboard-grid--editing' : ''}`}
            layouts={{ lg: activeView.layout.map((i) => ({ i: i.i, x: i.x, y: i.y, w: i.w, h: i.h })) }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 8, xs: 4, xxs: 2 }}
            rowHeight={32}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={editing}
            isResizable={editing}
            onLayoutChange={handleLayoutChange}
            draggableCancel='.fs-widget-handle'
          >
            {activeView.layout.map((item) => {
              const widget = findWidget(item.widgetId);
              if (!widget) return null;
              return (
                <Box key={item.i} sx={{ display: 'flex' }}>
                  <Paper sx={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: editing ? 'grab' : 'default' }}>
                    {editing && (
                      <Stack
                        direction='row'
                        spacing={0.5}
                        className='fs-widget-handle'
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          zIndex: 2,
                          bgcolor: 'rgba(255,255,255,0.95)',
                          border: '1px solid #dadde1',
                          borderRadius: 1,
                          px: 0.25,
                        }}
                      >
                        {widget.configurable && (
                          <Tooltip title='Konfigurera'>
                            <IconButton size='small' onClick={() => setConfigInstance(item)}>
                              <SettingsOutlined fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title='Ta bort'>
                          <IconButton size='small' onClick={() => handleRemove(item.i)}>
                            <DeleteOutlineOutlined fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    {widget.render(item)}
                  </Paper>
                </Box>
              );
            })}
          </ResponsiveGridLayout>
        </Box>

        {activeView.layout.length === 0 && (
          <Box
            sx={{
              border: '2px dashed #dadde1',
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: 13,
            }}
          >
            Tomt här. Klicka på &quot;Anpassa&quot; → &quot;Lägg till widget&quot; för att börja.
          </Box>
        )}

        <WidgetLibrary
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          onAdd={handleAddWidget}
          usedWidgetIds={new Set(activeView.layout.map((i) => i.widgetId))}
        />
        {configInstance?.widgetId === 'real-gifts-by-period' && (
          <GiftsByPeriodConfigDialog
            open={Boolean(configInstance)}
            instance={configInstance}
            onClose={() => setConfigInstance(null)}
            onSave={handleConfigSave}
          />
        )}
        {configInstance?.widgetId === 'real-new-members-by-period' && (
          <NewMembersByPeriodConfigDialog
            open={Boolean(configInstance)}
            instance={configInstance}
            onClose={() => setConfigInstance(null)}
            onSave={handleConfigSave}
          />
        )}
        {configInstance?.widgetId === 'real-members-by-profile' && (
          <RealMembersByProfileConfigDialog
            open={Boolean(configInstance)}
            instance={configInstance}
            onClose={() => setConfigInstance(null)}
            onSave={handleConfigSave}
          />
        )}
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth='xs' fullWidth>
          <DialogTitle>Återställ till standard?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Detta tar bort alla anpassningar och återställer dashboarden till standardvyerna. Vill du fortsätta?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setResetDialogOpen(false)}>Avbryt</Button>
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                setViews(DEFAULT_VIEWS);
                setEditing(false);
                setLibraryOpen(false);
                setResetDialogOpen(false);
              }}
            >
              Återställ
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardFilterProvider>
  );
};
